import { useState } from "react";
import { RotateCcw, ShieldCheck, XCircle } from "lucide-react";
import { useAuth } from "../auth/AuthProvider";
import { useOrganization } from "../organization/useOrganization";
import { Button } from "../../shared/ui/Button";
import { Card, EmptyState } from "../../shared/ui/Card";
import { PageSkeleton, StatusBadge } from "../../shared/ui/Status";
import { useScheduling, useSchedulingActions } from "./useScheduling";
export function ReversalApprovalPanel() {
  const { session } = useAuth();
  const org = useOrganization();
  const scheduling = useScheduling();
  const actions = useSchedulingActions();
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  if (!session || !org.data || !scheduling.data) return <PageSkeleton />;
  const schedules = Object.fromEntries(
    scheduling.data.schedules.map((s) => [s.id, s]),
  );
  const names = Object.fromEntries(
    org.data.accounts.map((a) => [a.id, a.name]),
  );
  const pending = scheduling.data.reversals.filter(
    (r) =>
      r.status === "pending" &&
      schedules[r.scheduleId]?.storeId === session.profile.storeId,
  );
  const run = async (id: string, decision: "approve" | "reject") => {
    if (
      decision === "approve" &&
      !window.confirm("确认批准撤销课消？系统将生成反向流水并退回课时。")
    )
      return;
    setNotice("");
    setError("");
    try {
      await actions.reviewReversal({ id, decision, reason: reasons[id] ?? "" });
      setNotice(
        decision === "approve"
          ? "撤销已批准，课时已通过反向流水退回。"
          : "撤销已驳回，原课消保持有效。",
      );
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "审批失败");
    }
  };
  return (
    <section className="page-stack reversal-panel">
      <div className="section-divider">
        <span>课消异常闭环</span>
        <h2>超时撤销审批</h2>
      </div>
      {(notice || error) && (
        <div
          className={
            error ? "inline-notice inline-notice--error" : "inline-notice"
          }
        >
          {error || notice}
        </div>
      )}
      {pending.length ? (
        <div className="approval-list">
          {pending.map((r) => (
            <Card key={r.id}>
              <div className="approval-order">
                <RotateCcw />
                <div>
                  <span>超过课消完成10分钟</span>
                  <h2>
                    {names[schedules[r.scheduleId]?.memberId]} · 申请人
                    {names[r.requestedBy]}
                  </h2>
                  <strong>撤销原因：{r.reason}</strong>
                </div>
                <StatusBadge tone="warning">待审批</StatusBadge>
              </div>
              <label className="v4-field">
                <span>复核意见</span>
                <input
                  value={reasons[r.id] ?? ""}
                  onChange={(e) =>
                    setReasons({ ...reasons, [r.id]: e.target.value })
                  }
                  placeholder="批准可留空，驳回必须填写"
                />
              </label>
              <div className="approval-actions">
                <Button
                  onClick={() => run(r.id, "approve")}
                  disabled={actions.pending}
                >
                  <ShieldCheck size={15} />
                  批准并退课时
                </Button>
                <Button
                  variant="danger"
                  onClick={() => run(r.id, "reject")}
                  disabled={actions.pending}
                >
                  <XCircle size={15} />
                  驳回
                </Button>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <EmptyState
          title="没有超时撤销申请"
          description="课消超过10分钟后，原操作员工提交的撤销会进入这里。"
        />
      )}
    </section>
  );
}
